import { useState, useEffect, useCallback } from 'react';
import { ArrowsClockwise, LinkSimpleHorizontal } from '@phosphor-icons/react';

import { getSpineManager, type IkConstraintInfo } from '../../lib/spine-manager';
import { getPixiApp } from '../../lib/pixi-app';
import { useSceneStore } from '../../stores/sceneStore';
import { Slider } from '../ui/slider';
import type { Layer } from '@spine-workspace/shared';

interface IKConstraintEditorProps {
  layer: Layer;
}

export default function IKConstraintEditor({ layer }: IKConstraintEditorProps) {
  const [constraints, setConstraints] = useState<IkConstraintInfo[]>([]);
  const { setLayerIkPose } = useSceneStore();

  const refresh = useCallback(() => {
    const c = getSpineManager().getIkConstraints(layer.id);
    if (c && c.length > 0) setConstraints(c);
  }, [layer.id]);

  // Initial fetch
  useEffect(() => { refresh(); }, [refresh]);

  // Per-frame sync so mix values stay current while IK handles are dragged
  useEffect(() => {
    const app = getPixiApp();
    if (!app) return;
    app.ticker.add(refresh);
    return () => { app.ticker?.remove(refresh); };
  }, [refresh]);

  if (constraints.length === 0) {
    return (
      <p className="text-text-tertiary text-[11px] font-sans italic">
        No IK constraints in this asset
      </p>
    );
  }

  function savePose(name: string, mix: number, bendDirection: 1 | -1) {
    const targets = getSpineManager().getIkTargets(layer.id);
    const t = targets?.find((t) => t.constraintName === name);
    if (!t) return;
    setLayerIkPose(layer.id, name, {
      localX: t.localX,
      localY: t.localY,
      mix,
      bendDirection,
    });
  }

  function handleMix(name: string, mix: number) {
    getSpineManager().setIkMix(layer.id, name, mix);
    const c = constraints.find((c) => c.name === name);
    if (c) savePose(name, mix, c.bendDirection);
  }

  function handleBendFlip(name: string, current: 1 | -1) {
    const next: 1 | -1 = current === 1 ? -1 : 1;
    getSpineManager().setIkBendDirection(layer.id, name, next);
    const c = constraints.find((c) => c.name === name);
    if (c) savePose(name, c.mix, next);
  }

  function handleResetPose(name: string) {
    getSpineManager().resetIkTarget(layer.id, name);
    const c = constraints.find((c) => c.name === name);
    if (c) savePose(name, c.mix, c.bendDirection);
    refresh();
  }

  return (
    <div className="space-y-4">
      {constraints.map((c) => (
        <div key={c.name} className="space-y-2">
          {/* Constraint name row */}
          <div className="flex items-center gap-2">
            <LinkSimpleHorizontal size={12} className="text-violet shrink-0" />
            <span className="font-mono text-xs text-accent font-medium truncate flex-1">{c.name}</span>
            <span className="text-[10px] text-text-tertiary font-sans bg-bg-elevated px-1.5 py-0.5 rounded shrink-0">
              {c.bones.length > 1 ? '2-bone' : '1-bone'}
            </span>
            <button
              title="Reset to setup pose"
              onClick={() => handleResetPose(c.name)}
              className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast shrink-0"
            >
              <ArrowsClockwise size={11} />
            </button>
          </div>

          {/* Chain info */}
          <div className="text-[10px] text-text-tertiary font-mono truncate">
            {c.bones.join(' → ')}{' '}
            <span className="text-violet">→ {c.targetBone}</span>
          </div>

          {/* Mix slider */}
          <div className="flex items-center gap-3">
            <span className="font-sans text-xs text-text-secondary w-[44px] shrink-0">Mix</span>
            <Slider
              min={0} max={1} step={0.01}
              value={[c.mix]}
              onValueChange={([v]) => handleMix(c.name, v)}
              className="flex-1"
            />
            <span className="font-mono text-xs text-text-tertiary w-[30px] text-right shrink-0">
              {Math.round(c.mix * 100)}%
            </span>
          </div>

          {/* Bend direction — only for 2-bone chains */}
          {c.bones.length > 1 && (
            <div className="flex items-center gap-3">
              <span className="font-sans text-xs text-text-secondary w-[44px] shrink-0">Bend</span>
              <button
                onClick={() => handleBendFlip(c.name, c.bendDirection)}
                className={`flex-1 px-3 py-[4px] rounded-md text-xs font-mono transition-all duration-fast border ${
                  c.bendDirection === 1
                    ? 'bg-accent-muted text-accent border-accent/30'
                    : 'bg-bg-input text-text-secondary border-border hover:bg-bg-hover'
                }`}
              >
                {c.bendDirection === 1 ? '+ positive' : '− negative'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
